import React, { useState, useEffect } from 'react';
import { HashRouter, Route, Routes, Link, useLocation } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import ShopPage from './pages/ShopPage';
import ProductDetail from './pages/ProductDetail';
import CheckoutPage from './pages/CheckoutPage';
import OrderTracking from './pages/OrderTracking';
import PublicTracking from './pages/PublicTracking';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import InfluencerPortal from './pages/InfluencerPortal';
import NotFoundPage from './pages/NotFoundPage';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminOrders from './pages/admin/AdminOrders';
import AdminProducts from './pages/admin/AdminProducts';
import AdminCustomers from './pages/admin/AdminCustomers';
import AdminShipping from './pages/admin/AdminShipping';
import AdminPaymentOptions from './pages/admin/AdminPaymentOptions';
import AdminInfluencers from './pages/admin/AdminInfluencers';
import AdminPromoCodes from './pages/admin/AdminPromoCodes';
import AdminLanding from './pages/admin/AdminLanding';
import AdminAboutContent from './pages/admin/AdminAboutContent';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import AdminSiteAnalytics from './pages/admin/AdminSiteAnalytics';
import AdminSettings from './pages/admin/AdminSettings';
import { trackSiteVisit, trackPageView } from './utils/analytics';
import './App.css';
import './pages/NotFoundPage.css';

const { FiCheck, FiShoppingBag, FiMessageCircle } = FiIcons;
const CART_STORAGE_KEY = 'darshana-cart';

function loadCart() {
  try {
    const savedCart = window.localStorage.getItem(CART_STORAGE_KEY);
    const parsedCart = savedCart ? JSON.parse(savedCart) : [];
    return Array.isArray(parsedCart) ? parsedCart : [];
  } catch {
    return [];
  }
}

function DirectPathRedirect() {
  useEffect(() => {
    const { hash, pathname, search } = window.location;

    if (!hash && pathname !== '/') {
      window.location.replace(`${window.location.origin}/#${pathname}${search}`);
    }
  }, []);

  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname + location.search;
    if (!path.startsWith('/admin')) {
      trackSiteVisit(path);
      trackPageView(path);
    }
  }, [location.pathname, location.search]);

  return null;
}

function FloatingCart({ cartCount }) {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  const isCheckout = location.pathname === '/checkout';

  if (cartCount <= 0 || isAdmin || isCheckout) return null;

  return (
    <Link to="/checkout" className="floating-cart" aria-label="View cart">
      <SafeIcon icon={FiShoppingBag} />
      <span className="floating-cart-badge">{cartCount}</span>
    </Link>
  );
}

function FloatingWhatsApp() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');

  if (isAdmin) return null;

  return (
    <a
      href="https://wa.me/94757895678"
      target="_blank"
      rel="noopener noreferrer"
      className="floating-whatsapp"
      aria-label="Chat on WhatsApp"
    >
      <SafeIcon icon={FiMessageCircle} />
    </a>
  );
}

function StoreLayout({ children, cartCount }) {
  return (
    <>
      <Navbar cartCount={cartCount} />
      {children}
      <Footer />
      <FloatingCart cartCount={cartCount} />
      <FloatingWhatsApp />
    </>
  );
}

function App() {
  const [cart, setCart] = useState(loadCart);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    try {
      window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
    } catch (error) {
      console.error('Could not save cart:', error);
    }
  }, [cart]);

  const addToCart = (product) => {
    setCart((current) => [...current, product]);
    setNotice(`${product.name} added to your cart`);
    window.setTimeout(() => setNotice(''), 2400);
    import('./utils/analytics').then(({ trackAddToCart }) => {
      trackAddToCart(product.id, product.name);
    });
  };

  return (
    <HashRouter>
      <DirectPathRedirect />
      <ScrollToTop />
      <AnalyticsTracker />
      <Routes>
        <Route path="/" element={<StoreLayout cartCount={cart.length}><HomePage onAdd={addToCart} /></StoreLayout>} />
        <Route path="/shop" element={<StoreLayout cartCount={cart.length}><ShopPage onAdd={addToCart} /></StoreLayout>} />
        <Route path="/about" element={<StoreLayout cartCount={cart.length}><AboutPage /></StoreLayout>} />
        <Route path="/contact" element={<StoreLayout cartCount={cart.length}><ContactPage /></StoreLayout>} />
        <Route path="/influencer/:token" element={<InfluencerPortal />} />
        <Route path="/product/:slug" element={<StoreLayout cartCount={cart.length}><ProductDetail onAdd={addToCart} /></StoreLayout>} />
        <Route path="/checkout" element={<StoreLayout cartCount={cart.length}><CheckoutPage cart={cart} setCart={setCart} /></StoreLayout>} />
        <Route path="/track" element={<StoreLayout cartCount={cart.length}><OrderTracking /></StoreLayout>} />
        <Route path="/track-public/:token" element={<StoreLayout cartCount={cart.length}><PublicTracking /></StoreLayout>} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/orders" element={<AdminOrders />} />
        <Route path="/admin/products" element={<AdminProducts />} />
        <Route path="/admin/customers" element={<AdminCustomers />} />
        <Route path="/admin/shipping" element={<AdminShipping />} />
        <Route path="/admin/payment-options" element={<AdminPaymentOptions />} />
        <Route path="/admin/influencers" element={<AdminInfluencers />} />
        <Route path="/admin/promo-codes" element={<AdminPromoCodes />} />
        <Route path="/admin/landing" element={<AdminLanding />} />
        <Route path="/admin/about-content" element={<AdminAboutContent />} />
        <Route path="/admin/analytics" element={<AdminAnalytics />} />
        <Route path="/admin/site-analytics" element={<AdminSiteAnalytics />} />
        <Route path="/admin/settings" element={<AdminSettings />} />
        <Route path="*" element={<StoreLayout cartCount={cart.length}><NotFoundPage /></StoreLayout>} />
      </Routes>
      {notice && <div className="toast"><SafeIcon icon={FiCheck} />{notice}</div>}
    </HashRouter>
  );
}

export default App;